import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useContent } from "../context/ContentContext";
import { waLink } from "../lib/whatsapp";

const WhatsAppButton = () => {
  const { lang } = useLang();
  const { CONTACT } = useContent();
  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setShow(true), 6500);
    return () => clearTimeout(id);
  }, []);

  const en = lang === "en";
  const quick = en
    ? ["I want to know about Hajj 2027 packages", "I want to know about Umrah packages", "I want to pre-register for Hajj", "I need help with my visa"]
    : ["হজ ২০২৭ প্যাকেজ সম্পর্কে জানতে চাই", "উমরাহ প্যাকেজ সম্পর্কে জানতে চাই", "হজ প্রি-রেজিস্ট্রেশন করতে চাই", "ভিসা নিয়ে সাহায্য দরকার"];
  const greet = (m) => `${en ? "Assalamu Alaikum" : "আসসালামু আলাইকুম"}, ${m}`;

  if (!show) return null;
  return (
    <div className="fixed bottom-6 right-6 z-[90] flex flex-col items-end gap-3" data-testid="whatsapp-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.25 }}
            className="w-80 rounded-3xl overflow-hidden shadow-lift bg-white border border-jannah-sand"
            data-testid="whatsapp-panel"
          >
            <div className="bg-[#075E54] text-white p-5 flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-white flex items-center justify-center overflow-hidden"><img src="/logo.png" alt="" className="w-9 h-9 object-contain" /></div>
              <div>
                <div className="font-bold">{CONTACT.person || "Fly Jannah"}</div>
                <div className="text-xs text-white/80 flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-[#25D366]" /> {en ? "Typically replies within minutes" : "সাধারণত কয়েক মিনিটে উত্তর দেয়"}</div>
              </div>
              <button onClick={() => setOpen(false)} className="ml-auto p-1.5 rounded-full hover:bg-white/15" aria-label="Close" data-testid="whatsapp-close"><X size={16} /></button>
            </div>
            <div className="p-4 bg-[#ECE5DD] space-y-2">
              <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 text-sm text-jannah-ink shadow-sm">
                {en ? "Assalamu Alaikum! How can we help you with your Hajj or Umrah journey?" : "আসসালামু আলাইকুম! আপনার হজ বা উমরাহ সফরে আমরা কীভাবে সাহায্য করতে পারি?"}
              </div>
              <div className="pt-1 grid gap-2">
                {quick.map((q, i) => (
                  <a key={i} href={waLink(CONTACT, greet(q))} target="_blank" rel="noreferrer" className="block text-left text-sm px-4 py-2.5 rounded-xl bg-white border border-jannah-sand hover:border-[#25D366] hover:bg-[#25D366]/5 transition-colors text-jannah-ink" data-testid={`whatsapp-quick-${i}`}>
                    {q}
                  </a>
                ))}
              </div>
              <a href={waLink(CONTACT, greet(en ? "I have a question." : "আমার একটি প্রশ্ন আছে।"))} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-2 w-full py-3 rounded-full bg-[#25D366] text-white font-bold text-sm hover:bg-[#1ebe5d]" data-testid="whatsapp-start-chat">
                <MessageCircle size={17} /> {en ? "Start chat on WhatsApp" : "হোয়াটসঅ্যাপে চ্যাট শুরু করুন"}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 260, damping: 18 }}
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat on WhatsApp"
        className="relative w-16 h-16 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-[0_12px_30px_-8px_rgba(37,211,102,0.7)] hover:scale-105 transition-transform"
        data-testid="whatsapp-fab"
      >
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
        <svg viewBox="0 0 32 32" className="w-8 h-8 relative" fill="currentColor"><path d="M16 3C9.4 3 4 8.3 4 14.9c0 2.3.7 4.5 1.9 6.4L4 29l7.9-1.9c1.8 1 3.9 1.5 6.1 1.5 6.6 0 12-5.3 12-11.9S22.6 3 16 3zm0 21.7c-1.9 0-3.8-.5-5.4-1.5l-.4-.2-4.7 1.1 1.2-4.4-.3-.4c-1.1-1.7-1.7-3.6-1.7-5.6 0-5.4 4.6-9.9 10.2-9.9s10.2 4.4 10.2 9.9-4.6 10-10.1 10zm5.6-7.4c-.3-.2-1.8-.9-2.1-1-.3-.1-.5-.2-.7.2-.2.3-.8 1-1 1.2-.2.2-.4.2-.7.1-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.5c.2-.2.2-.3.3-.5.1-.2.1-.4 0-.5-.1-.2-.7-1.7-1-2.3-.3-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1-1.1 2.5s1.1 2.9 1.2 3.1c.2.2 2.2 3.3 5.3 4.6.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4-.1-.1-.3-.2-.6-.4z" /></svg>
        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-jannah-crimson text-[10px] font-bold flex items-center justify-center border-2 border-white">1</span>
      </motion.button>
    </div>
  );
};

export default WhatsAppButton;
